// @flow

import { type AppValues } from '@tectonics/app-values';
import { type IncomingPayment, getCountry, getMerchant, getPaymentMethod } from '@tectonics/prs/incoming-payment';
import { type UserInputValues } from '@tectonics/prs/user-input-values';
import { type OutgoingPayment } from '@tectonics/prs/outgoing-payment';
import { threeDSecure } from './threedsecure';
import { ThreeDSecureError } from './threedsecure-error';
import { isBrowserExtensionError } from './is-browser-extension-error';
import ThreeDSecureModeResolver from '@tectonics/prs/threedsecure/threedsecure-mode-resolver';
import ThreedsecureMode from '@tectonics/prs/threedsecure/threedsecure-mode';

export async function threeDSecureOutgoingPaymentMapper(
  userInputValues: UserInputValues,
  incomingPayment: IncomingPayment,
  appValues: AppValues,
  outgoingPayment: $Shape<OutgoingPayment>,
): Promise<$Shape<OutgoingPayment>> {

  if (!await isPaymentMethodAllowed(incomingPayment, userInputValues)) {
    return outgoingPayment;
  }

  const threeDSecureMode:(ThreedsecureMode|null) = ThreeDSecureModeResolver.resolve(incomingPayment, userInputValues);
  if (threeDSecureMode === null) {
    return outgoingPayment;
  }

  try {
    const threeDSecureResponse = await threeDSecure(userInputValues, incomingPayment, threeDSecureMode);

    return {
      card: Object.assign({}, outgoingPayment.card, threeDSecureResponse),
    };
  } catch (ex) {
    const code = isBrowserExtensionError(ex) ? 'THREEDSECURE-EXT' : 'THREEDSECURE';
    throw new ThreeDSecureError('Ops, houve um erro com o seu pagamento :( Conclua o pagamento utilizando outro cartão ou método.', code);
  }
}

async function isPaymentMethodAllowed(incomingPayment: IncomingPayment, userInputValues: UserInputValues): Promise<boolean> {

  const selectedPaymentMethod = userInputValues['selectedPaymentMethod'];
  const selectedCardType = userInputValues['selectedCardType'];

  if (selectedPaymentMethod === 'creditcard' && selectedCardType === 'oneclick') {
    return false;
  }

  const country = getCountry(incomingPayment);
  const merchant = getMerchant(incomingPayment);

  if (!merchant.threedsecure) {
    return false;
  }

  const paymentMethod = getPaymentMethod(merchant.threedsecure, country, selectedPaymentMethod);
  if (paymentMethod === selectedPaymentMethod) {
    return true;
  }

  if (!merchant.threedsecure.allowed_cards) {
    return false;
  }

  const allowedCards = merchant.threedsecure.allowed_cards;
  const paymentMethodAllowed = allowedCards.find( card => card.type === selectedPaymentMethod );
  if (!paymentMethodAllowed || country !== 'br') {
    return false;
  }

  return true;
}
